import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, Heart, MessageSquare, AlertTriangle, Music, 
  UserX, Bot, Scale, Flag, Mail, FileText, CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CommunityGuidelinesPage() {
  return (
    <div className="min-h-screen py-12 px-4" data-testid="community-guidelines-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-secondary/20 text-secondary">Community Standards</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-4">
            Community Guidelines
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            DMV Soundstage is a creative platform built to support artists, creatives, and culture 
            across DC, Maryland, and Virginia. By using this platform, all users agree to follow 
            the guidelines below.
          </p>
        </div>

        {/* TL;DR Quick Rules */}
        <Card className="glass-card border-secondary/30 p-6 mb-8 bg-gradient-to-r from-secondary/10 to-primary/10">
          <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-secondary" />
            Quick Rules to Keep DMV Soundstage Strong
          </h2>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              Respect artists and creatives
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              No hate, harassment, or excessive negativity
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              Constructive feedback is welcome — toxic behavior is not
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              Only upload or link content you own or control
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              No spam, fake engagement, or manipulation
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-amber-400 font-bold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              3 strikes = permanent removal
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Let&apos;s build the DMV the right way.
            </p>
          </div>
        </Card>

        {/* Full Guidelines */}
        <div className="space-y-8">
          {/* Respect & Conduct */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-respect">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">Respect & Conduct</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Treat all artists, creators, and users with respect.</li>
                  <li>• Hate speech, slurs, discrimination, threats, harassment, or bullying are <span className="text-red-400 font-medium">strictly prohibited</span>.</li>
                  <li>• Constructive criticism is allowed; toxic behavior is not.</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* No Excessive Negativity */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-negativity">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">No Excessive Negativity</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Repeated negative comments meant to demean or discourage are not allowed.</li>
                  <li>• Trolling, baiting, dogpiling, or antagonizing artists is prohibited.</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* 3-Strike Policy */}
          <Card className="glass-card border-red-500/30 p-6 bg-red-500/5" data-testid="guidelines-strikes">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3 text-red-400">3-Strike Policy</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Users who violate community rules <span className="text-red-400 font-bold">three (3) times</span> will be permanently removed.</li>
                  <li>• Removal includes account deletion and loss of email access to the platform.</li>
                </ul>
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="font-bold text-amber-400">Strike 1</p>
                    <p className="text-xs text-muted-foreground">Warning</p>
                  </div>
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                    <p className="font-bold text-orange-400">Strike 2</p>
                    <p className="text-xs text-muted-foreground">Final Warning</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="font-bold text-red-400">Strike 3</p>
                    <p className="text-xs text-muted-foreground">Permanent Ban</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Profanity & Explicit Content */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-profanity">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">Profanity & Explicit Content</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Explicit language is allowed in music content if properly labeled.</li>
                  <li>• Excessive profanity, hate language, or graphic content is <span className="text-red-400">not allowed</span> in comments, bios, or public posts.</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Content Ownership */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-ownership">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <Music className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">Content Ownership</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Artists may only upload or link content they own or control the rights to.</li>
                  <li>• Music submissions must be officially released on DSPs where required.</li>
                  <li>• False ownership confirmations may result in content removal and account termination.</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* No Impersonation */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-impersonation">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                <UserX className="w-6 h-6 text-pink-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">No Impersonation or Misrepresentation</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• No impersonating other artists, brands, or public figures.</li>
                  <li>• Verification badges are admin-approved only.</li>
                  <li>• Fake industry roles or misleading credentials are prohibited.</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* No Spam or Manipulation */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-spam">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-6 h-6 text-cyan-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">No Spam or Manipulation</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• No bots, fake engagement, request manipulation, or chart abuse.</li>
                  <li>• Abnormal activity may be invalidated or penalized.</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Moderation & Enforcement */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-moderation">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-indigo-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">Moderation & Enforcement</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• DMV Soundstage uses AI moderation and manual review.</li>
                  <li>• The platform reserves the right to remove content or accounts that harm the community.</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Reporting & Appeals */}
          <Card className="glass-card border-border p-6" data-testid="guidelines-reporting">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                <Flag className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-3">Reporting & Appeals</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Users may report violations through platform tools.</li>
                  <li>• Appeals may be submitted to <a href="mailto:support@dmvsoundstage.com" className="text-secondary hover:underline">support@dmvsoundstage.com</a>.</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>

        {/* Footer Notice */}
        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>DMV Soundstage reserves the right to update these guidelines as the platform evolves.</p>
          <p className="mt-2">
            Questions? Contact us at{' '}
            <a href="mailto:support@dmvsoundstage.com" className="text-secondary hover:underline">
              support@dmvsoundstage.com
            </a>
          </p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/dmca" className="text-secondary hover:underline">DMCA Policy</Link>
            <span className="text-muted-foreground/40">|</span>
            <Link to="/terms" className="text-secondary hover:underline">Terms of Service</Link>
            <span className="text-muted-foreground/40">|</span>
            <Link to="/privacy" className="text-secondary hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
